import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { User, LoginCredentials, RegisterData, Login2faChallenge, isLogin2faChallenge } from '../types';
import apiService from '../services/api';
import { userHasPhoneRecord } from '../utils/phoneFormatUtils';
import { setBusiness2faTrustToken, markBusiness2faVerifiedThisSession, clearBusiness2faVerifiedThisSession } from '../utils/business2faTrust';

const AUTH_SESSION_INIT_KEY = 'authSessionInitialized_v1';

// Clear dashboard filter cache on a new auth/browser session so users land on "fresh defaults"
// (equivalent to clicking "Appointments / My Appointments" from the sidebar).
const clearBusinessDashboardAppointmentFilterCache = () => {
  try {
    localStorage.removeItem('businessAppointmentsFilters');
    localStorage.removeItem('businessMyAppointmentsFilters');
  } catch {
    // best-effort
  }
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<Login2faChallenge | void>;
  verifyLogin2fa: (challengeId: string, code: string, rememberBrowser?: boolean) => Promise<void>;
  resendLogin2fa: (challengeId: string) => Promise<Login2faChallenge>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const applyTheme = () => {
    try {
      const body = document.body;
      const classes = ['theme-neutral', 'theme-purple', 'theme-pink', 'theme-baby-blue'];
      body.classList.remove(...classes);
      body.classList.add('theme-purple');
    } catch {}
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');
      // Apply default purple theme
      applyTheme();

      // New browser session (tab closed / browser restarted) should not resurrect old dashboard filters.
      if (token && !sessionStorage.getItem(AUTH_SESSION_INIT_KEY)) {
        clearBusinessDashboardAppointmentFilterCache();
        sessionStorage.setItem(AUTH_SESSION_INIT_KEY, '1');
      }

      if (token && savedUser) {
        try {
          // Verify token is still valid first
          const currentUser = await apiService.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setUser(null);
          applyTheme();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<Login2faChallenge | void> => {
    try {
      const response = await apiService.login(credentials);
      if (isLogin2faChallenge(response)) {
        return response;
      }
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      applyTheme();

      // Fresh login should behave like a fresh dashboard entry.
      clearBusinessDashboardAppointmentFilterCache();
      sessionStorage.setItem(AUTH_SESSION_INIT_KEY, '1');

      // Business login that did not need OTP (no payments or trusted browser) is verified for this tab
      if (credentials.loginType === 'business') {
        markBusiness2faVerifiedThisSession(response.user.id);
      }
    } catch (error) {
      throw error;
    }
  };

  const verifyLogin2fa = async (
    challengeId: string,
    code: string,
    rememberBrowser?: boolean
  ): Promise<void> => {
    const response = await apiService.verifyLogin2fa(challengeId, code, rememberBrowser);
    localStorage.setItem('authToken', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    if (response.trustToken && response.user?.email) {
      setBusiness2faTrustToken(response.user.email, response.trustToken);
    }
    markBusiness2faVerifiedThisSession(response.user.id);
    setUser(response.user);
    applyTheme();
    clearBusinessDashboardAppointmentFilterCache();
    sessionStorage.setItem(AUTH_SESSION_INIT_KEY, '1');
  };

  const resendLogin2fa = async (challengeId: string): Promise<Login2faChallenge> => {
    return apiService.resendLogin2fa(challengeId);
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await apiService.register(data);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      applyTheme();

      clearBusinessDashboardAppointmentFilterCache();
      sessionStorage.setItem(AUTH_SESSION_INIT_KEY, '1');
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Even if logout fails, clear local storage
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      clearBusinessDashboardAppointmentFilterCache();
      clearBusiness2faVerifiedThisSession();
      try {
        sessionStorage.removeItem(AUTH_SESSION_INIT_KEY);
      } catch {
        // best-effort
      }
      setUser(null);
      applyTheme();
    }
  };

  const updateUser = useCallback((updatedUser: User) => {
    setUser((prev) => {
      const patch = Object.fromEntries(
        Object.entries(updatedUser as unknown as Record<string, unknown>).filter(([key, v]) => {
          if (v === undefined) return false;
          if (
            (key === 'phoneNumber' || key === 'phone' || key === 'phone_number') &&
            (v === null || v === '') &&
            prev != null &&
            userHasPhoneRecord(prev)
          ) {
            return false;
          }
          return true;
        })
      ) as Partial<User>;
      const newUser = { ...(prev ?? ({} as User)), ...patch } as User;
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
    applyTheme();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    login,
    verifyLogin2fa,
    resendLogin2fa,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
